import { TradingGridContainer, TradingGridItem, TradingLoadingButton, TradingTooltip } from '@trading-ui/core';
import TradingDatepicker from '@trading-ui/datepicker';
import { DownloadFileIcon } from '@trading-ui/icons';
import TradingSelect from '@trading-ui/select';
import { forwardRef, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import useAppContext from '@/hooks/contexts/useAppContext';
import useAppStore from '@/store/app.store';

import { AppUtils } from '@/utils/app.utils';

import { ROLES } from '@/constants/roles';

import type { ConsumptionTableResponse, StylesObject } from '@/interfaces';

import useConsumerStore from '../../store/consumer.store';

type Props = {
  canLoadData: boolean;
  loadData: () => void;
  isLoading: boolean;
  exportData: () => void;
  isExporting: boolean;
  tableData: ConsumptionTableResponse | undefined;
  isSmallScreen: boolean;
};

const styles = {
  inputs: {
    '.MuiAutocomplete-inputRoot': { maxHeight: '30px' },
    '.MuiAutocomplete-inputRoot .MuiAutocomplete-input': { minWidth: 0 },
  },
} as StylesObject;

const Actionbar = forwardRef<HTMLDivElement, Props>(
  ({ canLoadData, loadData, isLoading, exportData, isExporting, tableData, isSmallScreen = false }, ref) => {
    const { t } = useTranslation();
    const { language } = useAppContext();

    const user = useAppStore((state) => state.user);
    const masters = useAppStore((state) => state.masters);
    const { dateRange, company, cups, setDateRange, setCups, setCompaniesWithCupsCleanup } = useConsumerStore();

    const isAdmin = useMemo(() => user?.role === ROLES.MOEVE_ADMIN, [user]);

    // Handle companies change with automatic CUPS cleanup
    const handleCompaniesChange = (selectedCompany: typeof company) => {
      setCompaniesWithCupsCleanup(selectedCompany, masters.plants);
    };

    return (
      <div ref={ref}>
        <TradingGridContainer justifyContent="space-between" spacing={1}>
          <TradingGridItem xs={12} md={10} lg={9} xl={8}>
            <TradingGridContainer spacing={2}>
              <TradingGridItem xs={12} md={3}>
                <TradingDatepicker
                  label={t('consumer.filters.dateRangeLabel')}
                  fullWidth
                  onChange={(dates) => setDateRange(dates)}
                  startDate={dateRange?.[0] as Date}
                  endDate={dateRange?.[1] as Date}
                  isRange
                  locale={language === 'en' ? 'en-GB' : 'es'}
                />
              </TradingGridItem>
              {isAdmin ? (
                <>
                  <TradingGridItem xs={12} md={3}>
                    <TradingSelect
                      label={t('consumer.filters.companyLabel')}
                      value={company}
                      options={AppUtils.formatCompaniesToSelectOptions(masters.companies)}
                      onChange={handleCompaniesChange}
                      style={styles.inputs}
                    />
                  </TradingGridItem>
                  <TradingGridItem xs={12} md={3}>
                    <TradingTooltip title={!company ? t('consumer.filters.pleaseSelectCompanyFirst') : ''}>
                      <TradingSelect
                        label={t('consumer.filters.cupsLabel')}
                        value={cups}
                        options={AppUtils.filterCupsByCompanies(masters.plants, company)}
                        onChange={setCups}
                        multiple
                        disabled={!company}
                        style={styles.inputs}
                      />
                    </TradingTooltip>
                  </TradingGridItem>
                </>
              ) : (
                <TradingGridItem xs={12} md={3}>
                  <TradingSelect
                    label={t('consumer.filters.cupsLabel')}
                    value={cups}
                    options={AppUtils.formatCupsToSelectOptions(masters.plants)}
                    onChange={setCups}
                    multiple
                  />
                </TradingGridItem>
              )}
              <TradingGridItem xs={12} md={2}>
                <TradingLoadingButton
                  label={t('consumer.buttons.applyLabel')}
                  handleClick={loadData}
                  size="small"
                  disabled={!canLoadData}
                  loading={isLoading}
                  fullWidth={isSmallScreen}
                />
              </TradingGridItem>
            </TradingGridContainer>
          </TradingGridItem>
          <TradingGridItem xs={12} md={2} lg={3} xl={4} display="flex" justifyContent="flex-end">
            <TradingLoadingButton
              label={t('consumer.buttons.exportLabel')}
              endIcon={<DownloadFileIcon />}
              handleClick={exportData}
              loading={isExporting}
              disabled={!tableData?.consumptions.length}
              size="small"
              fullWidth={isSmallScreen}
            />
          </TradingGridItem>
        </TradingGridContainer>
      </div>
    );
  }
);

export default Actionbar;
